"use client";

import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Focus, Loader2, RefreshCw, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import { backendErrorMessage, useCameraFocus, useSetFocus } from "./api";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

const AF_MODE_NAMES: Record<number, TranslationKey> = {
  0: "cameras.control.modeManual",
  1: "cameras.control.modeAuto",
  2: "cameras.focus.modeContinuous",
};

export const FocusCard = ({ deviceId }: { deviceId: string }) => {
  const { t, formatNumber } = useLocalization();
  const focus = useCameraFocus(deviceId);
  const setFocus = useSetFocus(deviceId);

  const [lensPosition, setLensPosition] = useState(0);

  const formatValue = (value: number | null | undefined) =>
    value != null ? formatNumber(value) : "?";

  useEffect(() => {
    if (focus.data?.lens_position != null) {
      setLensPosition(Number(focus.data.lens_position.toFixed(2)));
    }
  }, [focus.data]);

  const applyManual = async () => {
    try {
      await setFocus.mutateAsync({ mode: "manual", lens_position: lensPosition });
      toast.success(
        t("cameras.focus.manualApplied", { position: formatValue(lensPosition) }),
      );
    } catch (error) {
      toast.error(t("cameras.focus.failed", { error: backendErrorMessage(error) }));
    }
  };

  const applyContinuous = async () => {
    try {
      await setFocus.mutateAsync({ mode: "continuous" });
      toast.success(t("cameras.focus.continuousEnabled"));
    } catch (error) {
      toast.error(t("cameras.focus.failed", { error: backendErrorMessage(error) }));
    }
  };

  const triggerAutofocus = async () => {
    try {
      // Full-frame AF scan; on success the camera locks the found position
      const result = await setFocus.mutateAsync({ mode: "auto" });
      if (result.success && result.autofocus?.locked) {
        toast.success(
          t("cameras.focus.focused", {
            position: formatValue(result.autofocus.lens_position),
            duration: formatValue(result.autofocus.duration_ms),
          }),
        );
      } else {
        toast.warning(
          result.error ??
            t("cameras.focus.notConverged", {
              state: result.autofocus?.af_state_name ?? t("cameras.focus.unknownState"),
            }),
        );
      }
    } catch (error) {
      toast.error(t("cameras.focus.autofocusFailed", { error: backendErrorMessage(error) }));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Focus className="h-4 w-4" />
            {t("cameras.focus.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            {focus.data && (
              <Badge variant="outline" className="font-mono text-xs">
                {t("cameras.focus.liveValues", {
                  mode:
                    focus.data.af_mode in AF_MODE_NAMES
                      ? t(AF_MODE_NAMES[focus.data.af_mode])
                      : focus.data.af_mode,
                  position:
                    focus.data.lens_position != null
                      ? formatNumber(focus.data.lens_position, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "?",
                  state: focus.data.af_state_name,
                })}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => focus.refetch()}
              disabled={focus.isFetching}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${focus.isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={triggerAutofocus}
            disabled={setFocus.isPending}
          >
            {setFocus.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <ScanSearch className="mr-1 h-3.5 w-3.5" />
            )}
            {t("cameras.focus.autofocus")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={applyContinuous}
            disabled={setFocus.isPending}
          >
            {t("cameras.focus.continuous")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("cameras.focus.roiHint", { button: t("cameras.photo.focusOnRoi") })}
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">
              {t("cameras.focus.lensPosition")}
            </Label>
            <Input
              type="number"
              min={0}
              max={15}
              step={0.05}
              value={lensPosition}
              onChange={(e) => setLensPosition(Number(e.target.value))}
              className="h-7 w-24 text-right"
            />
          </div>
          <Slider
            min={0}
            max={15}
            step={0.05}
            value={[lensPosition]}
            onValueChange={([value]) => setLensPosition(value)}
          />
          <Button
            size="sm"
            onClick={applyManual}
            disabled={setFocus.isPending}
            className="w-full"
          >
            {setFocus.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {t("cameras.focus.applyManual")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
